import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { FormatConfigService } from '../../../core/services/format-config.service';
import { ListLoader } from '../../components/list-loader/list-loader';
import { AbstractComponent } from '../../components/abstract-component/abstract.component';
import { LastRechtRootEntryVm } from '../../../core/services/standardrecht-viewmodel.service';
import { TableSortModel } from '../../tablesort/tableSortModel';
import { ComparatorService } from '../../services/comparator-service';
import { ComponentLocationStrategy } from '../../services/location-strategy/component-location-strategy';
import { StandardrechtLocationStrategy } from '../../services/location-strategy/standardrecht-location-strategy';
import { LinkComponent, NavTarget } from '../../components/link/link.component';
import { DokulinkItemBuilderFactory } from '../../components/dokulink/dokulinkItemBuilderFactory';
import { ErweiterungConfigResponse, PermissionResponse } from '../../../core/base-api';
import { ListLoaderWrapperComponent } from '../../components/list-loader-wrapper/list-loader-wrapper.component';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { HistoryDirective } from '../../directives/history.directive';
import { KompaktBelegComponent } from '../../kompaktdarstellung/beleg/kompakt-beleg.component';
import { KompaktStandardrechtComponent } from '../../kompaktdarstellung/standardrecht/kompakt-standardrecht.component';
import { KompaktTimepointComponent } from '../../kompaktdarstellung/timepoint/kompakt-timepoint.component';
import { DokulinkComponent } from '../../components/dokulink/dokulink.component';
import { MapsComponent } from '../maps/maps.component';
import { RechtsgrundComponent } from '../../kompaktdarstellung/rechtsgrund/rechtsgrund.component';
import { KompaktGrundstueckComponent } from '../../kompaktdarstellung/grundstueck/kompakt-grundstueck.component';
import { KompaktPersongbComponent } from '../../kompaktdarstellung/persongb/kompakt-persongb.component';
import {
  KompaktGrundpfandrechtComponent
} from '../../kompaktdarstellung/grundpfandrecht/kompakt-grundpfandrecht.component';
import { ReadMoreComponent } from '../read-more/read-more.component';
import { TranslatePipe } from '@ngx-translate/core';
import { DatumPipe } from '../../pipes/simple/datum.pipe';
import { FormatRechteIdPipe } from '../../pipes/format/format-rechte-id.pipe';
import { GrundstuecknummerPipe } from '../../pipes/simple/grundstuecknummer.pipe';
import { OrderByLastRechtPersonEntryVmPipe } from '../../pipes/order/order-by-gb-lastrechtpersonentry.pipe';
import { PersonGbListEntryMeta } from '../../../core/gb-api';
import { CapiLoginService } from '../../../core/extension/login/capi-login-service';
import { AppPermissionService } from '../../../core/services/app-permission.service';
import ActiveErweiterungenEnum = ErweiterungConfigResponse.ActiveErweiterungenEnum;
import AuskunftsperreEnum = PersonGbListEntryMeta.AuskunftsperreEnum;
import PermissionsEnum = PermissionResponse.PermissionsEnum;

@Component({
  selector: 'cap-standardrecht-table',
  templateUrl: './standardrecht-table.component.html',
  styleUrls: ['./standardrecht-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: ComponentLocationStrategy, useClass: StandardrechtLocationStrategy }],
  imports: [
    ListLoaderWrapperComponent,
    NgTemplateOutlet,
    NgClass,
    HistoryDirective,
    KompaktBelegComponent,
    LinkComponent,
    KompaktStandardrechtComponent,
    KompaktTimepointComponent,
    DokulinkComponent,
    MapsComponent,
    RechtsgrundComponent,
    KompaktGrundstueckComponent,
    KompaktPersongbComponent,
    KompaktGrundpfandrechtComponent,
    ReadMoreComponent,
    TranslatePipe,
    DatumPipe,
    FormatRechteIdPipe,
    GrundstuecknummerPipe,
    OrderByLastRechtPersonEntryVmPipe
  ]
})
export class StandardrechtTableComponent extends AbstractComponent implements OnInit, OnDestroy {
  @Input()
  formatKopfzeile: FormatKopfzeile = FormatKopfzeile.DATUM_BELEG;
  @Input()
  showGrundstuecke: boolean = true;
  @Input()
  showPersonen: boolean = true;
  @Input()
  showGehoertZu: boolean = true;
  @Input()
  showNachgang: boolean = true;
  @Input()
  showBemerkung: boolean = true;
  @Input()
  showVertragtext: boolean = true;
  @Input()
  hasRight: boolean = true;
  @Input()
  rechtArt: RechtArt = RechtArt.DEFAULT;
  @Input()
  customSort: (a, b) => number;

  grundstueckNavTarget: NavTarget = NavTarget.NAV_GRUNDSTUECK;
  formatKopfzeileEnum = FormatKopfzeile;
  tableSortModel = new TableSortModel<LastRechtRootEntryVm>('table', this.comparatorService);
  isDokulinkConfigured: boolean = false;
  isGeschaeftsmodulCapWebErweiterung: boolean;
  keineTranslateKeys: string = 'Keine';

  protected hasPermissionGeschaeftPdfLink: boolean = false;
  protected readonly AuskunftsperreEnum = AuskunftsperreEnum;
  private changeDetectionTimeout: any;

  constructor(
    protected router: Router,
    protected appformatConfigService: FormatConfigService,
    protected permissionService: AppPermissionService,
    protected comparatorService: ComparatorService,
    protected loginService: CapiLoginService,
    private cdr: ChangeDetectorRef,
    protected dokuLinkBuilderFactory: DokulinkItemBuilderFactory
  ) {
    super();
    keysToTranslate<string>([
      'Belastet',
      'Berechtigt',
      'BelastetBerechtigt',
      'ZuLasten',
      'ZuGunsten',
      'ZuGunstenLasten',
      'Bezeichnung',
      'DatumOderBelegOderRechtsgrund',
      'gehoertZu',
      'KeineBzwGemaessGrundbuch',
      'DatumEntstehung',
      'DominantIndet'
    ]);
  }

  private _listLoader: ListLoader<LastRechtRootEntryVm>;

  get listLoader(): ListLoader<LastRechtRootEntryVm> {
    return this._listLoader;
  }

  @Input()
  set listLoader(listLoader: ListLoader<LastRechtRootEntryVm>) {
    if (!listLoader) {
      return;
    }
    this._listLoader = listLoader;
    this._listLoader.applySortModel(this.tableSortModel);
  }

  ngOnInit(): void {
    this.activateChangeDetectionTrigger();
    if (this.customSort) {
      this.tableSortModel.addColumn('EnstehungSort', (a, b) => this.customSort(a, b)).switchSortDirection();
    } else {
      this.tableSortModel
        .addColumn('EnstehungSort', (a, b) => this.comparatorService.lastRechtRootEntryVmComparator(a, b))
        .switchSortDirection();
    }
    this.addSub(
      this.appformatConfigService.settingsConfig$.subscribe((config) => {
        this.isDokulinkConfigured = config?.isDokuLinkRechtConfigured;
      })
    );
    this.addSub(
      this.appformatConfigService.erweiterungsConfig$.subscribe((config) => {
        this.isGeschaeftsmodulCapWebErweiterung = config?.activeErweiterungen.includes(ActiveErweiterungenEnum.GESCHAEFTSMODUL_CAP_WEB);
      })
    );
    this.hasPermissionGeschaeftPdfLink = this.permissionService.hasPermission(PermissionsEnum.RECHT_PDF_LINK);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    clearTimeout(this.changeDetectionTimeout);
  }

  private activateChangeDetectionTrigger() {
    this.changeDetectionTimeout = setTimeout(() => {
      this.cdr.markForCheck();
      this.activateChangeDetectionTrigger();
    }, 3000);
  }
}

export enum FormatKopfzeile {
  GRUNDSTUECK,
  DATUM_BELEG
}

export enum RechtArt {
  DEFAULT,
  ANMERKUNG,
  VORMERKUNG
}
